import { Component, OnDestroy, effect, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { AgreementService, AgreementTemplateService, loadPadConnector } from '@beautyapp/core';
import { InputModule } from '@beautyapp/form';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef } from 'ng-zorro-antd/modal';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import { SelectComponent } from '../../../../../../form/src/lib/select/select.component';

/**
 * Versioned in the URL: if this contract is ever replaced wholesale, bumping
 * this to v2 stops a stale cached copy from silently serving the old one.
 * It does NOT protect against a non-breaking ADDITION to this same v1
 * contract -- releasePad was added to the vendored asset without a filename
 * bump, so a browser holding a cached pre-releasePad v1 script keeps
 * answering `typeof connector.releasePad === 'function'` as false
 * indefinitely. The guards at every releasePad call site stop that from
 * throwing, but they cannot make the release happen: on such a machine,
 * every releasePad call silently no-ops, so after the first patient every
 * later /pad/show returns pad-busy and the release button reports failure
 * (see PAD_RELEASE_FAILED_MESSAGE) instead of actually fixing anything.
 * There is no frontend-only fix for this -- it needs either a genuine
 * filename bump (a decision for whoever owns the vendored asset, not made
 * here) or a server-side cache-control policy on this path.
 */
const PAD_CONNECTOR_SRC = '/assets/pad-connector/pad-connector.v1.global.js';

/**
 * Generic "could not reach the pad" message. Used for the connector-load /
 * showOnPad-threw / unrecognised-result branches -- the ones where the
 * failure genuinely looks like a hardware/bridge problem. Extracted to a
 * single constant because it used to appear four times in this file, which
 * is what let the create-for-pad failure (a backend problem, not a pad
 * problem) hide behind the same wording -- see PAD_CREATE_FAILED_MESSAGE.
 */
export const PAD_CONNECT_FAILED_MESSAGE =
  'ხელმოწერის პადთან დაკავშირება ვერ მოხერხდა. გამოიყენეთ SMS კოდი.';

/**
 * Distinct from PAD_CONNECT_FAILED_MESSAGE on purpose: this fires when
 * agreement/create-for-pad itself fails (e.g. a 404/500), which is a
 * backend problem, not a pad-hardware problem. Telling staff "the pad is
 * unreachable" when the real cause is a failed API call sends them to
 * debug the wrong thing.
 */
export const PAD_CREATE_FAILED_MESSAGE =
  'თანხმობის ჩანაწერის შექმნა ვერ მოხერხდა. სცადეთ ხელახლა ან გამოიყენეთ SMS კოდი.';

/** The bridge allows one active pad session; this names that specific, fixable cause. */
export const PAD_BUSY_MESSAGE =
  'პადი უკვე აჩვენებს სხვა დოკუმენტს. გაათავისუფლეთ პადი ან გამოიყენეთ SMS კოდი.';

/** releaseStuckPadSession() couldn't confirm the bridge actually released the session. */
export const PAD_RELEASE_FAILED_MESSAGE =
  'პადის გათავისუფლება ვერ მოხერხდა. სცადეთ ხელახლა ან გამოიყენეთ SMS კოდი.';

export const PAD_SIGN_SAVE_FAILED_MESSAGE =
  'ხელმოწერის შენახვა ვერ მოხერხდა. სცადეთ ხელახლა ან გამოიყენეთ SMS კოდი.';

/**
 * resendCode() (agreement/{id}/resend-sms-code) had no error handling at
 * all -- a 400 (e.g. the patient has no valid mobilePhone on file, which
 * the backend rejects with BadRequestException) left codeSent false,
 * rendered nothing, and the "Send SMS" button appeared to silently do
 * nothing. This is the one message shown outside the pad-sign-panel, since
 * resendCode() can fire before codeSent() is ever true (send()'s reuse
 * branch, when a pad-created agreement already exists).
 */
export const SMS_SEND_FAILED_MESSAGE =
  'SMS-ის გაგზავნა ვერ მოხერხდა. შეამოწმეთ პაციენტის ტელეფონის ნომერი ან სცადეთ ხელახლა.';

/** Shown after ACTIVE_TIMEOUT_MS of no signature, so staff get a prompt to fall back instead of watching an indefinite "waiting" message. */
export const PAD_ACTIVE_TOO_LONG_MESSAGE =
  'პადი დიდხანს ელოდება — შეამოწმეთ მოწყობილობა, ან გამოიყენეთ SMS კოდი.';

const ACTIVE_TIMEOUT_MS = 60000;

/**
 * The frame the connector hands back on a signature event. `strokes` is the
 * raw coordinate time-series -- the whole design exists to avoid storing it,
 * so it is named here only so it can be typed and deliberately ignored, never
 * spread into what gets sent to the backend.
 */
type SignatureFrame = {
  signature: string;
  sigstring: string;
  strokes?: unknown;
};

type ShowOnPadResult = {
  ok: boolean;
  mode: 'pad' | 'fallback' | 'error';
  sessionId?: string;
  error?: string;
  subscription?: { close: () => void };
};

type PadConnectorApi = {
  showOnPad: (
    url: string,
    opts: { onSignature: (frame: SignatureFrame) => void }
  ) => Promise<ShowOnPadResult>;
  /**
   * Releases the bridge's single active pad session (POSTs /pad/idle) and
   * closes any subscription it is still tracking for that session id. Must
   * be called whenever this component stops caring about a session it
   * opened -- the bridge answers a second /pad/show with 409 pad-busy for
   * every patient after until someone does. Optional in the type (not just
   * defensively guarded at call sites) because a browser can have an old
   * cached copy of the connector asset predating this method -- see the
   * PAD_CONNECTOR_SRC comment.
   */
  releasePad?: (opts: { sessionId?: string }) => Promise<{ ok: boolean; released: boolean }>;
};

/**
 * The visible state of the "sign on pad" action. `fallback` (no pad on this
 * machine) and `forbidden-origin` (this machine's pad bridge has not been
 * configured to trust beautyapp's origin) are both expected, everyday
 * outcomes, not errors -- they get their own states so the message shown can
 * be specific instead of a generic failure. `busy` means the bridge already
 * has a different session open (most often: a previous attempt was never
 * released). Only `error` is truly unexpected.
 */
type PadSignState =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'fallback'
  | 'forbidden-origin'
  | 'busy'
  | 'error'
  | 'signed';

@UntilDestroy()
@Component({
  selector: 'beautyapp-choose-agreement',
  imports: [
    SelectComponent,
    ReactiveFormsModule,
    InputModule,
    NzButtonComponent,
    NzIconDirective,
    TranslatePipe,
    NzGridModule,
    NzModalFooterDirective
],
  templateUrl: './choose-agreement.component.html',
  styleUrl: './choose-agreement.component.scss',
  standalone: true
})
export class ChooseAgreementComponent implements OnDestroy {
  // Referenced from the template so the 'active' case's elapsed-time text
  // stays sourced from the one exported constant rather than a duplicated
  // literal.
  protected readonly PAD_ACTIVE_TOO_LONG_MESSAGE = PAD_ACTIVE_TOO_LONG_MESSAGE;

  agreementTemplateService = inject(AgreementTemplateService);
  agreementService = inject(AgreementService);
  modalRef = inject(NzModalRef);
  data = inject(NZ_MODAL_DATA);
  private store = inject(Store);

  /**
   * Read from the store rather than fetched: the active branch is already
   * there, so this costs no request -- same approach as the cashier flag
   * (libs/cashier/.../cashier.component.ts). `=== true` because the column
   * is nullable and absent must mean disabled.
   */
  readonly padEnabled = this.store.selectSnapshot(AuthState.getActiveBranch)
    ?.signaturePadEnabled === true;

  /** Shown when agreement/create-for-pad or agreement/accept-with-signature
   *  403s because the flag was switched off mid-session, after the pad
   *  button had already rendered -- see isPadDisabledError(). Distinct from
   *  PAD_CREATE_FAILED_MESSAGE / PAD_SIGN_SAVE_FAILED_MESSAGE so staff see
   *  the actual, fixable cause instead of a generic failure. */
  readonly PAD_DISABLED_MESSAGE =
    'ხელმოწერის პადი არ არის ჩართული ამ ფილიალისთვის.';

  agreement = signal<any>(null)

  agreementTemplateResource = rxResource({
    stream: () => this.agreementTemplateService.dropdown()
  });

  form: FormGroup = new FormGroup({
    agreementTemplateId: new FormControl(null, Validators.required),
    code: new FormControl(null)
  });

  codeSent = signal(false);
  // Shown outside the pad-sign-panel (see the note on SMS_SEND_FAILED_MESSAGE):
  // resendCode() can fire before codeSent() is ever true, so this cannot live
  // behind the same `@if (codeSent())` gate as the OTP input without becoming
  // exactly the kind of silent failure it exists to surface.
  smsMessage = signal<string | null>(null);

  // The pad-signing alternative to the SMS/OTP flow above. It is additive:
  // nothing in signOnPad() ever touches `form` or `codeSent`, so a clinic
  // without a pad (or one whose bridge does not yet trust beautyapp's
  // origin) keeps the SMS path exactly as it works today.
  padState = signal<PadSignState>('idle');
  padMessage = signal<string | null>(null);
  // True once a live pad session has been 'active' for ACTIVE_TIMEOUT_MS
  // with no signature -- the relay is push-only, so without this, staff
  // watching "waiting for the patient's signature" have no prompt telling
  // them to check the hardware or fall back to SMS.
  padActiveTooLong = signal(false);

  private padSubscription: { close: () => void } | null = null;
  // Set once a pad session is actually open (`result.ok`), cleared once it
  // is released. See releaseSession() -- the single place all three of
  // these fields are torn down together.
  private padSessionId: string | null = null;
  private padConnector: PadConnectorApi | null = null;
  // The CONFLICTING session's id from a 409 pad-busy response. Most likely
  // a session this very component opened earlier and never released, so it
  // is kept rather than discarded -- releaseStuckPadSession() lets staff
  // act on it directly, and releaseSession() prefers it over an id-less
  // release when retrying from 'busy' (see the comment there).
  private padBusySessionId: string | null = null;
  private destroyed = false;
  // Tracks the template control's own last-seen value so a valueChanges
  // emission that doesn't actually change the selection (some selects fire
  // on a no-op re-click; ours might too, and there is no guarantee this
  // stays true forever) is a no-op here rather than discarding an
  // in-progress OTP step or a live pad session for nothing.
  private lastTemplateId: unknown = null;
  // Identity, not value, is what makes staleness detection correct: a
  // switch-away-and-back sequence (A -> B -> A) restores the SAME template
  // value while an attempt for the original A is still in flight, which a
  // value-only comparison cannot distinguish from "never left A". Every
  // reset and every fresh signOnPad() attempt bumps this counter, and each
  // attempt captures it at start -- a mismatch by the time an await
  // resolves means the world moved on, regardless of what the template
  // control's value happens to read now.
  private padAttempt = 0;

  constructor() {
    this.lastTemplateId = this.form.controls['agreementTemplateId'].value;

    // A changed template selection invalidates whatever agreement is
    // currently held, in both directions: an SMS already sent for template
    // A must not be silently reused when staff switch to template B and hit
    // the pad, and a pad-created record for A must not survive into an SMS
    // send for B. Discarding an in-progress OTP step here is correct -- the
    // code the patient is holding belongs to the template that was just
    // deselected.
    this.form.controls['agreementTemplateId'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value === this.lastTemplateId) {
          return;
        }
        this.lastTemplateId = value;
        this.padAttempt++;

        // A live pad session must not survive the reset (Critical C): the
        // button would otherwise re-enable (padState -> idle below) while
        // the bridge still has the OLD template's session open, and the
        // next attempt would either collide with it (409 pad-busy) or --
        // worse -- overwrite its handle with null, leaking the socket for
        // the rest of the page's life.
        this.releaseSession();

        this.agreement.set(null);
        this.codeSent.set(false);
        this.padState.set('idle');
        this.padMessage.set(null);
        this.smsMessage.set(null);

        // setValidators alone does not revalidate -- it would leave a
        // dormant `required` armed against the (now cleared) code control,
        // which reset() DOES revalidate against, permanently invalidating
        // the form with no way for staff to clear it (the code input only
        // renders once codeSent is true again, and codeSent can only be
        // set by send()/resendCode(), both gated on form.invalid). Clearing
        // the validator first is what makes reset() safe to call here.
        const code = this.form.get('code');
        code?.clearValidators();
        code?.reset();
      });

    // Drives padActiveTooLong. Re-runs on every padState() change, and
    // effect()'s cleanup callback (via onCleanup) cancels any pending timer
    // automatically -- both when padState changes again before the timeout
    // fires, and on component destroy -- so no separate field-tracking or
    // manual clearTimeout() calls are needed at every place padState can
    // change away from 'active'.
    effect((onCleanup) => {
      if (this.padState() !== 'active') {
        this.padActiveTooLong.set(false);
        return;
      }
      const handle = setTimeout(() => this.padActiveTooLong.set(true), ACTIVE_TIMEOUT_MS);
      onCleanup(() => clearTimeout(handle));
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.padAttempt++;
    this.releaseSession();
    // Purely for consistency with the reset handler -- nothing re-renders
    // a destroyed component, so this is not user-visible, but leaving
    // padState frozen mid-flight ('connecting'/'active') on a component
    // nobody will ever read again is an accident of early-returning, not a
    // deliberate signal worth preserving.
    this.padState.set('idle');
    this.padMessage.set(null);
  }

  /**
   * Closes any socket this component is holding and tells the bridge the
   * session is over, if a connector was ever loaded. Idempotent -- safe to
   * call whether or not a session is actually open. The single choke point
   * for every path that must give up a session: normal teardown, a
   * template change while a session is live, and the top of every fresh
   * signOnPad() attempt (so a session stranded by a reset can never be
   * silently overwritten by the next one).
   *
   * Prefers padSessionId (this component's own tracked, live session) over
   * padBusySessionId (a CONFLICTING session merely observed via a 409) when
   * choosing what to pass as sessionId. What that id actually guarantees is
   * narrower than it looks: in the vendored connector, sessionId scopes only
   * WHICH of this page's own locally-tracked WebSocket subscriptions get
   * closed (closeTrackedSubscriptions) -- the bridge-side POST /pad/idle
   * that actually ends a session is unconditional and carries no id, so it
   * always ends whatever the bridge currently has open, regardless of what
   * (if anything) is passed here. Passing a known id does NOT protect the
   * bridge's current document from an unwanted release; only not calling
   * releasePad() at all when nothing should be released does that. What
   * passing a known id over an id-less call DOES protect is a DIFFERENT
   * beautyapp tab's own tracked subscription on this same workstation,
   * which an id-less call would otherwise close for no reason.
   */
  private releaseSession(): void {
    const connector = this.padConnector;
    const sessionId = this.padSessionId ?? this.padBusySessionId;
    this.clearPadTracking();

    if (connector && typeof connector.releasePad === 'function') {
      void connector.releasePad({ sessionId: sessionId ?? undefined });
    }
  }

  /**
   * Nulls every piece of session-tracking state this component holds AND
   * closes the socket, without notifying the bridge. Self-contained and
   * idempotent (safe to call whether or not anything is actually open) --
   * shared by releaseSession() (which also calls releasePad) and
   * releaseStuckPadSession()'s success branch (which has already called
   * releasePad itself, with the specific known session id, and must not
   * fire a second, id-less release) -- keeping the field-clearing in one
   * place is what stops the two from drifting apart again. Before this,
   * releaseStuckPadSession() cleared padBusySessionId but left padConnector
   * set, so the NEXT signOnPad() attempt's own releaseSession() call would
   * find a leftover connector and fire an UNWANTED releasePad() call at
   * all -- and because the bridge's POST /pad/idle is unconditional (see
   * releaseSession()'s doc comment), any such call ends whatever session
   * the bridge currently has open, not just one this component tracked.
   */
  private clearPadTracking(): void {
    this.padSubscription?.close();
    this.padSubscription = null;
    this.padSessionId = null;
    this.padConnector = null;
    this.padBusySessionId = null;
  }

  /**
   * Staff's manual way out of an 'active' session (wrong patient/template
   * selected, or the pad just seems unresponsive) -- releases this
   * component's own live session and returns to idle so the pad button
   * re-enables immediately. Also the user-facing escape hatch for Critical
   * C: previously there was no way to end a live session without either
   * signing or navigating away.
   */
  cancelPadSession(): void {
    this.releaseSession();
    this.padState.set('idle');
    this.padMessage.set(null);
  }

  /**
   * pad-busy means a DIFFERENT session -- most likely one this same
   * component opened earlier and never released -- is occupying the
   * bridge's single slot. The 409 response carries that session's id
   * (stashed in padBusySessionId when the busy state is entered); this
   * lets staff release it directly instead of the id being discarded and
   * staff being stuck with only "busy, use SMS" and no recovery.
   *
   * Waits for the bridge's own confirmation (`released === true`) before
   * clearing the busy state -- releasePad resolves `{ ok:false,
   * released:false }` when the bridge itself is unreachable, and silently
   * returning to idle in that case would tell staff the pad is free when
   * it is not, sending them straight back into another 409.
   *
   * This is the only async path in the file that awaits a real network
   * call (the connector's fetch to the bridge) without re-checking
   * staleness afterward -- every other one (signOnPad()'s three awaits)
   * captures `attempt` and re-validates via isStaleAttempt() once the
   * await resolves. Without that here, a fresh signOnPad() attempt started
   * while this method's fetch is still in flight would have its OWN
   * padConnector/padSessionId/padSubscription silently wiped out by this
   * method's success branch once the fetch finally resolves -- a live
   * session for a genuinely new patient, gone, with the recovery button
   * itself now dead (!padConnector short-circuits it at the top).
   */
  async releaseStuckPadSession(): Promise<void> {
    const sessionId = this.padBusySessionId;
    const connector = this.padConnector;
    if (!sessionId || !connector || typeof connector.releasePad !== 'function') {
      return;
    }

    // Captured WITHOUT incrementing padAttempt -- this call is not itself a
    // new attempt, and bumping the counter here would wrongly invalidate a
    // signOnPad() attempt that legitimately starts (and finishes) while
    // this await is in flight.
    const attempt = this.padAttempt;
    const selectedTemplateId = this.form.controls['agreementTemplateId'].value;

    let result: { released?: boolean } | undefined;
    try {
      result = await connector.releasePad({ sessionId });
    } catch {
      result = undefined;
    }

    if (this.isStaleAttempt(attempt, selectedTemplateId)) {
      return;
    }

    if (result?.released === true) {
      // Must clear padConnector here too, not just padBusySessionId (see
      // clearPadTracking()'s doc comment for why a leftover connector is
      // itself the hazard).
      this.clearPadTracking();
      this.padState.set('idle');
      this.padMessage.set(null);
    } else {
      this.padMessage.set(PAD_RELEASE_FAILED_MESSAGE);
    }
  }

  resendCode() {
    this.agreementService.resendCode(this.agreement().id, this.data.userId)
      .pipe(
        untilDestroyed(this),
      )
      .subscribe({
        next: (res) => {
          if (res) {
            this.smsMessage.set(null);
            this.codeSent.set(true);
            const code = this.form.get('code');
            code?.setValidators(Validators.required);
            // setValidators alone leaves the rule dormant -- without this, the
            // Accept button (bound to form.invalid) reads valid/enabled with
            // an empty code until something else happens to trigger
            // revalidation.
            code?.updateValueAndValidity();
          } else {
            console.error('resendCode: empty response');
            this.smsMessage.set(SMS_SEND_FAILED_MESSAGE);
          }
        },
        error: (error) => {
          // The primary staff action for a pad-created agreement's SMS path
          // (send() short-circuits into resendCode() whenever an agreement
          // already exists) had no error handling at all. The backend 400s
          // this with "Invalid mobile phone number" when the patient has
          // none on file -- an ordinary clinic scenario, not an edge case --
          // and with no handler here, codeSent stayed false, nothing
          // rendered, and the Send SMS button appeared to do nothing.
          console.error('Error resending code:', error);
          this.smsMessage.set(SMS_SEND_FAILED_MESSAGE);
        },
      });
  }

  send() {
    if (this.agreement()?.id) {
      // The pad path (or a previous send()) already created a record for
      // the template currently selected -- resend on it instead of minting
      // a second, orphaned agreement for the same consent. This holds for
      // both creation routes: createWithSms and createForPad both write
      // `agreementId: dto.agreementTemplateId` on the record, and the
      // constructor's valueChanges reset (synchronous) plus signOnPad's
      // post-await staleness checks (asynchronous, identity-based -- see
      // isStaleAttempt) both guarantee `agreement` can never survive a
      // template change it doesn't belong to -- so by the time send()
      // runs, this is always the *current* template's record.
      this.resendCode();
      return;
    }

    this.agreementService.create({
      agreementTemplateId: this.form.controls['agreementTemplateId'].value,
      userId: this.data.userId,
      serviceId: this.data.serviceId,
      bookingId: this.data.bookingId
    }).pipe(
      untilDestroyed(this),
    ).subscribe({
      next: (res) => {
        if (res) {
          this.agreement.set(res);
          this.codeSent.set(true);
          const code = this.form.get('code');
          code?.setValidators(Validators.required);
          // Same reasoning as resendCode(): without this, the Accept
          // button is enabled with an empty code immediately after send().
          code?.updateValueAndValidity();
        }
      },
      error: (error) => {
        console.error('Error creating agreement:', error);
        // Handle error appropriately, e.g., show a notification
      }
    });
  }

  accept() {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.agreementService.acceptWithOtp({
      id: this.agreement().id,
      smsCode: this.form.value.code
    }).pipe(
      untilDestroyed(this),
    ).subscribe({
      next: (res) => {
        if (res) {
          this.modalRef.close(res);
        }
      },
      error: (error) => {
        console.error('Error accepting agreement:', error);
        // Handle error appropriately, e.g., show a notification
      }
    });
  }

  async signOnPad(): Promise<void> {
    // The only caller today is the pad button in the template, which the
    // @if (padEnabled) wrapper around .pad-sign-panel already removes when
    // the flag is off -- so every padState/padMessage render site is
    // unreachable in that case too. This method is public, though, so a
    // future caller invoking it directly while the flag is off would set
    // state with nowhere to render it: a silent failure. Guarding here,
    // not just in the template, is the same belt-and-braces this component
    // already applies elsewhere (see releaseSession()'s own top-of-attempt
    // call) -- cheap defence in depth for a component whose history is
    // losing guarantees to new off-switches.
    if (!this.padEnabled) {
      return;
    }

    // A second click while a session is already being opened or is live
    // must be a no-op: re-entering here would overwrite padSubscription /
    // padSessionId with a fresh (or null) value and strand the first
    // session (see Critical 2/Important 3 in the review).
    if (this.padState() === 'connecting' || this.padState() === 'active') {
      return;
    }

    // Identifies THIS attempt. Captured before any await; compared against
    // this.padAttempt (which every reset and every fresh attempt bumps)
    // after each await, so a stale attempt is detected by identity even if
    // the template selection later returns to the same value it started
    // with (switch-away-and-back) -- a value-only comparison cannot tell
    // those two situations apart.
    const attempt = ++this.padAttempt;

    // A fresh attempt must never inherit a session stranded by an earlier
    // one -- e.g. a template change reset padState to 'idle' (re-enabling
    // this button) without a user action ever releasing a session that was
    // live at the time (Critical C). Belt-and-braces alongside the reset's
    // own call to the same method: whichever path got there first has
    // already made this a no-op.
    this.releaseSession();

    const selectedTemplateId = this.form.controls['agreementTemplateId'].value;
    let agreement = this.agreement();

    // Belt-and-braces: the constructor's valueChanges subscription already
    // clears `agreement` the moment the template selection changes, so this
    // branch should be unreachable in the real UI. It stays as a second,
    // independent check because reuse-without-verification is exactly the
    // bug this whole method exists to avoid, and a signal can be set
    // directly (tests do this; a future code path might too).
    if (agreement && agreement.agreementId !== selectedTemplateId) {
      agreement = null;
    }

    this.padState.set('connecting');
    this.padMessage.set(null);

    if (!agreement?.id) {
      if (!selectedTemplateId) {
        this.padState.set('idle');
        return;
      }

      try {
        agreement = await firstValueFrom(
          this.agreementService.createForPad({
            agreementTemplateId: selectedTemplateId,
            userId: this.data.userId,
            serviceId: this.data.serviceId,
            bookingId: this.data.bookingId,
          })
        );
      } catch (error) {
        console.error('Error creating agreement for pad:', error);
        if (!this.isStaleAttempt(attempt, selectedTemplateId)) {
          this.padState.set('error');
          // Reachable when the branch's flag is switched off mid-session,
          // after the pad button already rendered -- name the actual cause
          // instead of the generic create-failure message.
          this.padMessage.set(
            this.isPadDisabledError(error) ? this.PAD_DISABLED_MESSAGE : PAD_CREATE_FAILED_MESSAGE
          );
        }
        return;
      }

      // The component may have been destroyed, staff may have changed the
      // template selection, or -- switch-away-and-back -- a NEWER attempt
      // may already be running for the SAME template this one started
      // with (Critical B / the concurrency window). Any of the three
      // means: bail before ever touching the pad bridge or the
      // `agreement` signal. Nothing has been opened yet, so there is
      // nothing to release, but proceeding could show a stale template's
      // document on the pad, or clobber a newer attempt's state, with no
      // visible sign anything was wrong.
      if (this.isStaleAttempt(attempt, selectedTemplateId)) {
        return;
      }

      if (!agreement?.id) {
        this.padState.set('error');
        this.padMessage.set(PAD_CREATE_FAILED_MESSAGE);
        return;
      }

      this.agreement.set(agreement);
    }

    const id = agreement.id;

    try {
      const joyusing = (await loadPadConnector(
        PAD_CONNECTOR_SRC
      )) as PadConnectorApi;

      if (this.isStaleAttempt(attempt, selectedTemplateId)) {
        return;
      }
      this.padConnector = joyusing;

      const padSignUrl = `${window.location.origin}/pad-sign/${id}`;

      const result = await joyusing.showOnPad(padSignUrl, {
        onSignature: (frame: SignatureFrame) => {
          if (this.destroyed) {
            return;
          }
          // Named fields only -- never `{ ...frame }`. The frame also
          // carries `strokes`, the raw coordinate array this design exists
          // to keep out of the database, and `sigstring` must go through
          // untouched because the backend stores it byte-for-byte as
          // evidence.
          this.agreementService
            .acceptWithSignature({
              id,
              signaturePng: frame.signature,
              sigstring: frame.sigstring,
            })
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (res) => {
                this.padState.set('signed');
                this.padMessage.set(null);
                if (res) {
                  this.modalRef.close(res);
                }
              },
              error: (error) => {
                this.padState.set('error');
                // The backend guards accept-with-signature the same way it
                // guards create-for-pad: the flag can be switched off
                // between the pad button rendering and the patient actually
                // signing, and staff should see the real cause here too,
                // not the generic save-failure message.
                this.padMessage.set(
                  this.isPadDisabledError(error)
                    ? this.PAD_DISABLED_MESSAGE
                    : PAD_SIGN_SAVE_FAILED_MESSAGE
                );
              },
            });
        },
      });

      if (this.isStaleAttempt(attempt, selectedTemplateId)) {
        // The modal is gone, the template selection moved on, or a newer
        // attempt has already taken over, before the bridge answered.
        // Never surface state for a request nobody is looking at anymore,
        // and never leave a live session or socket behind for the next
        // patient (Critical 2/3/B): close the socket the caller would
        // otherwise never get a handle to, and release the bridge session
        // if one was actually opened.
        result.subscription?.close();
        if (result.ok && typeof joyusing.releasePad === 'function') {
          void joyusing.releasePad({ sessionId: result.sessionId });
        }
        return;
      }

      this.padSubscription = result.subscription ?? null;

      if (result.ok) {
        this.padSessionId = result.sessionId ?? null;
        this.padState.set('active');
        this.padMessage.set(null);
      } else if (result.mode === 'fallback') {
        // Not an error: no pad on this machine, or the bridge is
        // unreachable. The SMS path stays exactly as it was.
        this.padState.set('fallback');
        this.padMessage.set(
          'ხელმოწერის პადი ამ კომპიუტერზე არ არის ხელმისაწვდომი. გამოიყენეთ SMS კოდი.'
        );
      } else if (result.error === 'forbidden-origin') {
        // Distinct from both `fallback` and a generic error: the bridge
        // exists and was reached, but this machine's bridge configuration
        // does not yet list beautyapp's origin as allowed. This will be the
        // most common first-run failure in every clinic, so it gets a
        // message that names the actual, fixable problem.
        this.padState.set('forbidden-origin');
        this.padMessage.set(
          'ამ კომპიუტერის ხელმოწერის მოწყობილობის ბრიჯს არ აქვს beautyapp-ის მისამართი დამატებული დაშვებულ წყაროებში. მიმართეთ IT-ს ბრიჯის კონფიგურაციის გასაახლებლად, ან გამოიყენეთ SMS კოდი.'
        );
      } else if (result.error === 'pad-busy') {
        // The bridge allows exactly one active session. Most often this is
        // an earlier attempt that was never released -- name that, and
        // keep the conflicting session's id so staff can release it
        // directly (releaseStuckPadSession()) rather than being told only
        // "busy" with no way out.
        this.padBusySessionId = result.sessionId ?? null;
        this.padState.set('busy');
        this.padMessage.set(PAD_BUSY_MESSAGE);
      } else {
        this.padState.set('error');
        this.padMessage.set(PAD_CONNECT_FAILED_MESSAGE);
      }
    } catch {
      // loadPadConnector rejected, or showOnPad itself threw -- the SMS
      // path must still be usable.
      if (!this.isStaleAttempt(attempt, selectedTemplateId)) {
        this.padState.set('error');
        this.padMessage.set(PAD_CONNECT_FAILED_MESSAGE);
      }
    }
  }

  private isStaleAttempt(attempt: number, selectedTemplateId: unknown): boolean {
    return (
      this.destroyed ||
      attempt !== this.padAttempt ||
      this.form.controls['agreementTemplateId'].value !== selectedTemplateId
    );
  }

  /**
   * True for the specific 403 the backend returns from both
   * agreement/create-for-pad and agreement/accept-with-signature when the
   * caller's branch is not enabled for the pad -- see the backend contract
   * (agreement.controller / pad-signature.guard). Shared by both call sites
   * so a branch-disabled failure surfaces the same, distinct
   * PAD_DISABLED_MESSAGE regardless of which of the two requests hit it,
   * rather than one of them silently falling back to a generic message.
   */
  private isPadDisabledError(error: unknown): boolean {
    return (
      (error as any)?.status === 403 &&
      (error as any)?.error?.message === 'signature-pad-disabled'
    );
  }
}
